import React, { useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AdminSideMenu from "./components/SIdeMenu/AdminSideMenu";
import SuperAdminSideMenu from "./components/SIdeMenu/SuperAdminSideMenu";
import Navbar from "./components/Navbar/Navbar";
import ManagerSideMenu from "./components/SIdeMenu/ManagerSideMenu";
import LoanAccountSideMenu from "./components/SIdeMenu/LoanAccountSideMenu";
import KeepAliveOutlet from "./components/Router/KeepAliveOutlet";
import { PageRefreshProvider } from "./context/PageRefreshContext";
import RefreshPageButton from "./components/Buttons/RefreshPageButton";
import { getSessionRole } from "./utils/auth";
import { getNavMenuItems, resolveNavTitle } from "./utils/navTitle";
import { FiArrowLeft, FiMenu, FiX } from "react-icons/fi";

const SIDE_MENU_STORAGE_KEY = "icd-side-menu-open";

const readSideMenuOpen = () => {
  try {
    return localStorage.getItem(SIDE_MENU_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const Layout = () => {
  const sessionRole = getSessionRole();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [sideMenuOpen, setSideMenuOpen] = useState(readSideMenuOpen);

  const pageTitle = useMemo(() => {
    const items = getNavMenuItems();
    return resolveNavTitle(location.pathname, items);
  }, [location.pathname]);

  const SideNav =
    sessionRole === 0
      ? SuperAdminSideMenu
      : sessionRole === 1
      ? AdminSideMenu
      : sessionRole === 4
      ? LoanAccountSideMenu
      : ManagerSideMenu;

  const toggleSideMenu = () => {
    setSideMenuOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDE_MENU_STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <PageRefreshProvider>
      <div className="w-screen flex bg-page h-screen overflow-hidden max850:flex-col max850:relative">
        <div
          className={`shrink-0 max850:hidden overflow-hidden transition-[width] duration-300 ease-in-out ${
            sideMenuOpen ? "w-[220px]" : "w-0"
          }`}
          aria-hidden={!sideMenuOpen}
        >
          <SideNav />
        </div>
        <Navbar />

        <div
          ref={scrollRef}
          className="flex-1 min-w-0 min-h-0 w-full flex flex-col overflow-y-auto bg-page max850:pt-[4.25rem] max850:mt-0 scrollable"
        >
          <div className="sticky top-0 z-40 shrink-0 max850:hidden border-b border-line/80 bg-page/95 backdrop-blur-md">
            <div className="flex items-center gap-2.5 px-3 sm:px-4 py-2.5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-line bg-card text-ink text-sm font-bold shadow-soft hover:bg-subtle hover:border-main/30 hover:text-main transition-colors active:scale-[.98]"
              >
                <FiArrowLeft size={17} />
                <span>Back</span>
              </button>
              <button
                type="button"
                onClick={toggleSideMenu}
                aria-label={sideMenuOpen ? "Hide menu" : "Show menu"}
                aria-expanded={sideMenuOpen}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-line bg-card text-ink text-sm font-bold shadow-soft hover:bg-subtle hover:border-main/30 hover:text-main transition-colors active:scale-[.98]"
              >
                {sideMenuOpen ? <FiX size={17} /> : <FiMenu size={17} />}
                <span>{sideMenuOpen ? "Hide menu" : "Menu"}</span>
              </button>
              <div className="flex-1 min-w-0 flex flex-col items-center justify-center px-2">
                <h1 className="w-full text-center text-[15px] font-bold text-ink truncate leading-tight">
                  {pageTitle}
                </h1>
              </div>
              <RefreshPageButton />
            </div>
          </div>

          <div className="flex-1 min-h-0 w-full px-3 sm:px-4 pt-3 sm:pt-4 pb-2">
            {sessionRole === 0 ? (
              <KeepAliveOutlet scrollContainerRef={scrollRef} />
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      </div>
    </PageRefreshProvider>
  );
};

export default Layout;
